import { AppPathType, HostProtocol } from '@iotize/tap';
import { converters } from '@iotize/tap/service/core';
import {
  LoginCredential,
  LowPowerOptimizationLevel,
  MultiRequestFrame,
  MultiResponseFrame,
  NfcPairingMode,
  SecurityOptions,
  SecurityOptionsConverter,
} from '@iotize/tap/service/impl/interface';
import { GET, POST, PUT } from '../annotations';
import { DEFAULT_HOST_INACTIVITY_PERIOD } from '../config';
import {
  ascii,
  enumIntToEnumStringArrayConverter,
  enumIntToEnumStringConverter,
  hostProtocols,
  loginCredential,
  macAddress,
  multiRequests,
  multiResponses,
  nfcPairingMode,
  powerOptimizationLevel,
  uint16,
  uint32,
} from '../converters';
import { TapServerError } from '../tap-server-error';
import { CallContext, VirtualTap } from '../virtual-tap-server';

const availableHostProtocolsConverter = enumIntToEnumStringArrayConverter(
  hostProtocols,
  {
    BLE: HostProtocol.BLE,
    WIFI: HostProtocol.WIFI,
    NFC: HostProtocol.NFC,
  }
);

export class InterfaceService {
  keepAliveCounter = 0;

  get applicationData() {
    if (!this.server.config.application) {
      this.server.config.application = {};
    }
    return this.server.config.application;
  }

  get wirelessConfig() {
    if (!this.server.config.wireless) {
      this.server.config.wireless = {};
    }
    return this.server.config.wireless;
  }
  get tapConfig() {
    if (!this.server.config.tap) {
      this.server.config.tap = {};
    }
    return this.server.config.tap;
  }

  get dataConfig() {
    if (!this.server.config.data) {
      this.server.config.data = {};
    }
    return this.server.config.data;
  }

  // get wifiConfig() {
  //     if (!this.tapConfig.wifi) {
  //         this.tapConfig.wifi = {};
  //     }
  //     return this.tapConfig.wifi;
  // }

  constructor(public server: VirtualTap) {
    this.server.bindConfigToRoute({
      path: '/1024//9',
      converter: ascii,
      default: 'TAP',
      item: () => this.applicationData,
      key: 'name',
      get: true,
      put: true,
    });
    this.server.bindConfigToRoute({
      path: '/1024//10',
      converter: converters.stringVersion,
      // converter: new StringVersionConverter(),
      default: '1.0.0',
      item: () => this.server.config,
      key: 'version',
      post: false,
      get: true,
      put: true,
    });
    this.server.bindConfigToRoute({
      path: '/1024//13',
      converter: enumIntToEnumStringConverter(nfcPairingMode, NfcPairingMode),
      default: 'NO',
      item: () => {
        if (!this.wirelessConfig.protocols) {
          this.wirelessConfig.protocols = {};
        }
        if (!this.wirelessConfig.protocols.nfc) {
          this.wirelessConfig.protocols.nfc = {};
        }
        return this.wirelessConfig.protocols.nfc;
      },
      key: 'pairingMode',
      get: true,
      put: true,
    });
    this.server.bindConfigToRoute({
      path: '/1024//14',
      converter: enumIntToEnumStringConverter(
        powerOptimizationLevel,
        LowPowerOptimizationLevel
      ),
      default: 'NO',
      item: () => this.tapConfig,
      key: 'powerOptimization',
      get: true,
      put: true,
    });
    this.server.bindConfigToRoute({
      path: '/1024//19',
      item: () => this.applicationData,
      key: 'universalLink',
      default: '',
      converter: ascii,
      get: true,
      put: true,
    });

    this.server.bindConfigToRoute({
      // Authorized host protocol
      path: '/1024//17',
      item: () => {
        if (!this.wirelessConfig.options) {
          this.wirelessConfig.options = {};
        }
        return this.wirelessConfig.options;
      },
      key: 'authorizedProtocols',
      converter: {
        encode: (i) => {
          if (!i || i.length === 0) {
            i = ['BLE', 'WIFI', 'NFC'];
          }
          return availableHostProtocolsConverter.encode(i as any);
        },
        decode: (i) => {
          return availableHostProtocolsConverter.decode(i) as any; // TODO
        },
      },
      default: [],
      get: true,
      put: true,
    });

    this.server.bindConfigToRoute({
      path: '/1024//36',
      item: () => this.applicationData,
      key: 'data',
      // converter: ,
      default: new Uint8Array(),
      get: true,
      put: true,
    });

    this.server.bindConfigToRoute({
      path: '/1024//84',
      item: () => this.applicationData,
      key: 'productCode',
      converter: ascii,
      default: '',
      get: true,
      put: true,
    });
  }

  @POST({
    path: '/1024//0',
    payloadType: loginCredential,
  })
  public login(context: CallContext<LoginCredential>) {
    return this.server.login(context.body);
  }

  @POST({
    path: '/1024//1',
  })
  public logout() {
    return this.server.logout();
  }

  @GET({
    path: '/1024//2',
    responseType: uint16,
  })
  public getProfileId() {
    return this.server.sessionState?.groupId || 0;
  }

  @GET({
    path: '/1024//3',
    responseType: uint16,
  })
  public getCurrentHostProtocol(context: CallContext) {
    return context.protocol.getType();
  }

  @GET({
    path: '/1024//6',
    responseType: new SecurityOptionsConverter(),
  })
  public getLock() {
    return this.server.securityOptions;
  }

  @GET({
    path: '/1024//4',
    responseType: uint32,
  })
  public keepAlive() {
    return this.keepAliveCounter++;
  }

  @PUT({
    path: '/1024//6',
    payloadType: new SecurityOptionsConverter(),
    auth: ['admin'],
  })
  public putLock(lock: SecurityOptions) {
    this.tapConfig.security = lock;
  }

  @GET({
    path: '/1024//7',
    responseType: uint16,
  })
  public getCurrentGroupId() {
    return this.server.sessionState.groupId;
  }

  @POST({
    path: '/1024//10',
    auth: ['admin', 'supervisor'],
  })
  public reloadConfig() {}

  @GET({
    path: '/1024//11',
    responseType: ascii,
  })
  public getAppPath() {
    if (typeof this.applicationData.uri === 'object') {
      return `${AppPathType[this.applicationData.uri.type]}${
        this.applicationData.uri.value
      }`;
    }
    return this.applicationData.uri || '';
  }

  @PUT({
    path: '/1024//11',
    payloadType: ascii,
  })
  public putAppPath(data: string) {
    this.applicationData.uri = data;
  }

  @GET({
    path: '/1024//21',
    responseType: hostProtocols,
  })
  public getAvailableHostProtocols() {
    return this.server.getHostProtocols();
  }

  @GET({
    path: '/1024//22',
    responseType: macAddress,
  })
  public getMacAddress() {
    return this.server.getMacAddress();
  }

  // @GET({
  //     path: '/1024//36'
  // })
  // public getApplicationParameters() {
  //     return this.server.config.data.applicationParameters;
  // }

  @GET({
    path: '/1024//34',
    responseType: uint16,
  })
  public getHostInactivityPeriod() {
    return (
      this.server.config?.wireless?.options?.inactivityTimeout ||
      DEFAULT_HOST_INACTIVITY_PERIOD
    );
  }

  @PUT({
    path: '/1024//34',
    responseType: uint16,
  })
  public setHostInactivityPeriod(context: CallContext<number>) {
    if (!this.server.config.wireless) {
      this.server.config.wireless = {};
    }
    if (!this.server.config.wireless.options) {
      this.server.config.wireless.options = {};
    }
    this.server.config.wireless.options.inactivityTimeout = context.body;
  }

  @GET({
    path: '/1024//35',
    responseType: ascii,
  })
  public getTapCurrentHostname() {
    return this.server.getHostName();
  }

  @GET({
    path: '/1024//60',
    responseType: ascii,
  })
  public getProductKey() {
    // TODO typing
    return (
      (this.server.config.tap as any)?.productKey ||
      `${this.server.serialNumber}_0000000000`
    );
  }

  @GET({
    path: '/1024//89',
    responseType: multiResponses,
    payloadType: multiRequests,
  })
  public multiRequest(context: CallContext<MultiRequestFrame>) {
    const body = context.body;
    const result: MultiResponseFrame = {
      responses: [],
    };
    for (const container of body.requests) {
      const response = this.server.handleRequest(container.request, context);

      result.responses.push({
        response,
        responseLen: 1 + response.data.length,
      });
    }
    return result;
  }
}
